import {
  useContext,
  useState,
  Fragment,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MobXProviderContext } from "mobx-react";
import classNames from "classnames";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { InfoButtonGray } from "assets/img";
import {
  getValidMealListForSubsidy,
  useMealNextAvailableDateForSubsidy,
  useMealNextAvailableDate,
  getValidMealList,
} from "utils/hooks/useMealNextAvailableDate";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { Calendar } from "react-multi-date-picker";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import object from "utils/helpers/object";
import { priceFormatter, preventMinus } from "utils/helpers/validation";
import { useSubsidyControl } from "utils/hooks/useSubsidyControl";
import { ToggleButton } from "components";

const allDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function ChangeSubsidyMealPlanUsers({
  visible,
  setModal,
  bulkUserUpdate = false,
  bulkUsers,
  updatingMeal = false,
  meal,
  meals,
}) {
  const [subsidyType, setSubsidyType] = useState("daily");
  const [weeklySubsidy, setWeeklySubsidy] = useState("0.00");
  const [dailySubsidy, setDailySubsidy] = useState({});
  const [overPayment, setOverPayment] = useState(true);
  const { mealManagementStore } = useContext(MobXProviderContext);
  const mealPlan = mealManagementStore.mealPlan;
  const { userStore } = useContext(MobXProviderContext);
  const { locationID, defaultLocation } = userStore;

  const [toggleStates, setToggleStates] = useState({});
  const calendarRef = useRef(null);
  const mealDateUser = useMealNextAvailableDate(meals);
  const mealDateSubsidy = useMealNextAvailableDateForSubsidy(meals);
  const validMealsUser = getValidMealList(meals);
  const validMealsSubsidy = getValidMealListForSubsidy(meals);
  const nextAvailableMeal = bulkUserUpdate ? mealDateUser : mealDateSubsidy;
  const validMeals = bulkUserUpdate ? validMealsUser : validMealsSubsidy;
  const [changeEffect, setChangeEffect] = useState("next");
  const [selectedCustomDate, setSelectedCustomDate] = useState(null);
  const [warning, setWarning] = useState(false);
  const [weeklySubsidyError, setWeeklySubsidyError] = useState(false);

  const enabledDays = useMemo(() => {
    return mealPlan?.attributes?.days || [];
  }, [mealPlan]);

  const {
    dailySubsidy: controlledSubsidy,

    isDayEnabled,
    setSubsidyManually,
  } = useSubsidyControl(enabledDays);

  useEffect(() => {
    if (!bulkUserUpdate) {
      if (updatingMeal) {
        const rawSubsidy = toJS(meal?.attributes?.daily_subsidy) || {};
        const formattedSubsidy = Object.fromEntries(
          Object.entries(rawSubsidy)?.map(([day, value]) => [
            day,
            parseFloat(value).toFixed(2),
          ])
        );

        setSubsidyManually(formattedSubsidy);
        setSubsidyType(meal?.attributes?.subsidy_type ?? "daily");
        setWeeklySubsidy(meal?.attributes?.subsidy ?? 0);
        setOverPayment(meal?.attributes?.employee_pays_by_credit_card);
      } else {
        const rawSubsidy = toJS(mealPlan?.attributes?.daily_subsidy) || {};
        const formattedSubsidy = Object.fromEntries(
          Object.entries(rawSubsidy)?.map(([day, value]) => [
            day,
            parseFloat(value).toFixed(2),
          ])
        );

        const rawSubsidyWeekly = parseFloat(mealPlan?.attributes?.subsidy);
        setWeeklySubsidy(
          isNaN(rawSubsidyWeekly) ? "0.00" : rawSubsidy.toFixed(2)
        );
        setSubsidyManually(formattedSubsidy);
        setSubsidyType(mealPlan?.attributes?.subsidy_type ?? "daily");
        setOverPayment(mealPlan?.attributes?.employee_pays_by_credit_card);
      }
    } else if (bulkUsers?.length === 1) {
      const singleUser = bulkUsers[0];

      const isCustom = !!singleUser?.subsidy_type;
      const userSubsidy = singleUser?.daily_subsidy || {};
      const defaultSubsidy = mealPlan?.attributes?.daily_subsidy || {};
      const defaultSubsidyType = mealPlan?.attributes?.subsidy_type;
      const defaultWeekly = mealPlan?.attributes?.subsidy;

      const toggles = {};
      const finalSubsidy = {};

      allDays.forEach((day) => {
        if (isCustom) {
          toggles[day] = userSubsidy?.[day] !== undefined;

          finalSubsidy[day] = toggles[day]
            ? parseFloat(userSubsidy[day]).toFixed(2)
            : parseFloat(defaultSubsidy?.[day] ?? 0).toFixed(2);
        } else {
          toggles[day] = false;
          finalSubsidy[day] = parseFloat(defaultSubsidy?.[day] ?? 0).toFixed(2);
        }
      });

      const rawWeekly =
        singleUser?.subsidy != null
          ? parseFloat(singleUser.subsidy)
          : parseFloat(defaultWeekly);
      const formattedWeekly = isNaN(rawWeekly) ? "0.00" : rawWeekly.toFixed(2);

      setDailySubsidy(finalSubsidy);
      setSubsidyType(singleUser?.subsidy_type ?? defaultSubsidyType ?? "daily");
      setWeeklySubsidy(formattedWeekly);
      setToggleStates(toggles);
    } else {
      setSubsidyType(mealPlan?.attributes?.subsidy_type);
      setWeeklySubsidy(mealPlan?.attributes?.subsidy);
      setDailySubsidy({
        monday: "0.00",
        tuesday: "0.00",
        wednesday: "0.00",
        thursday: "0.00",
        friday: "0.00",
        saturday: "0.00",
        sunday: "0.00",
      });
    }
  }, [mealPlan]);

  const onSavePress = () => {
    if (subsidyType === "daily") {
      const missingFields = Object.entries(toggleStates ?? {}).some(
        ([day, isToggled]) =>
          isToggled && (dailySubsidy[day] === "" || dailySubsidy[day] == null)
      );

      if (missingFields) {
        return;
      }
    }

    if (subsidyType === "weekly" && (!weeklySubsidy || weeklySubsidy === "")) {
      setWeeklySubsidyError(true);
      return; // prevent submission
    }

    setWeeklySubsidyError(false);

    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
    } else if (bulkUserUpdate) {
      const allTogglesOff = !Object.values(toggleStates).some(Boolean);
      const filteredDailySubsidy = Object.fromEntries(
        Object.entries(dailySubsidy).filter(([day]) => toggleStates?.[day])
      );

      let payload = {
        ids: bulkUsers?.map((user) => user?.id),
        employee_meal_plan: {
          subsidy_type:
            allTogglesOff && subsidyType === "daily" ? null : subsidyType,
          daily_subsidy:
            allTogglesOff && subsidyType === "daily"
              ? null
              : filteredDailySubsidy,
          subsidy:
            allTogglesOff && subsidyType === "daily" ? null : weeklySubsidy,
        },
        meal_series_id: mealPlan?.id,
      };

      if (object.isPresent(nextAvailableMeal)) {
        payload.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .bulkSubsidyUpdate(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    }
  };

  const onChangeDailySubsidy = (value, day) => {
    let daily = dailySubsidy;
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (regex.test(value)) {
      daily[day] = value;

      setDailySubsidy({ ...daily });
    }
  };

  const onChangeDaily = (value, day) => {
    let daily = dailySubsidy;

    daily[day] = value;

    setDailySubsidy({ ...daily });
  };

  const setEnableOverpayment = (value) => {
    setOverPayment(value);
  };

  const handleDateChange = (value) => {
    const selectedDate = dayjs(value.toDate()).format("YYYY-MM-DD");

    setSelectedCustomDate(selectedDate);
  };

  const onToggleChange = (day, value) => {
    setToggleStates((prev) => ({ ...prev, [day]: value }));

    if (value) {
      let defaultValue = 0;

      if (bulkUserUpdate && bulkUsers?.[0]) {
        const singleUser = bulkUsers[0];
        const isCustom = !!singleUser?.subsidy_type;

        defaultValue = isCustom
          ? singleUser?.daily_subsidy?.[day] ?? 0
          : mealPlan?.attributes?.daily_subsidy?.[day] ?? 0;
      } else {
        defaultValue = mealPlan?.attributes?.daily_subsidy?.[day] ?? 0;
      }

      setDailySubsidy((prev) => ({
        ...prev,
        [day]: parseFloat(defaultValue).toFixed(2),
      }));
    } else {
      setDailySubsidy((prev) => ({
        ...prev,
        [day]: "",
      }));
    }
  };

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {warning ? (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="flex justify-between items-center mb-[22px]">
                      <button
                        className="font-inter-regular text-[16px] text-hh-accent-dark"
                        onClick={() => {
                          setWarning(false);
                          setSelectedCustomDate(null);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="xs"
                          className="mr-2"
                          style={{
                            color: "#130f26",
                          }}
                        />
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={() => setModal(false)}
                        className="btn-style"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="2x"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <h2 className="text-2xl font-inter-bold text-dark-gray">
                      Set Date for Subsidy Changes
                    </h2>

                    <span className="bg-light-gray d-col mt-[16px] py-[12px] rounded-lg">
                      {bulkUsers?.length > 1 && (
                        <small className="font-inter-regular text-dark-gray text-[16px] capitalize bg-light-gray px-[8px] mb-[8px] rounded-lg">
                          {bulkUsers?.length} Users
                        </small>
                      )}
                      <div className="bg-light-gray px-[8px] rounded-lg">
                        {subsidyType === "daily" ? (
                          <div className="d-col">
                            Subsidy updates:
                            {[
                              { key: "monday", label: "Mon" },
                              { key: "tuesday", label: "Tue" },
                              { key: "wednesday", label: "Wed" },
                              { key: "thursday", label: "Thu" },
                              { key: "friday", label: "Fri" },
                              { key: "saturday", label: "Sat" },
                              { key: "sunday", label: "Sun" },
                            ]
                              // 🟢 Skip days with 0, "0", "0.00", null, undefined
                              .filter(
                                (day) => Number(dailySubsidy?.[day.key]) > 0
                              )
                              .map((day) => (
                                <small
                                  key={day.key}
                                  className="font-inter-regular d-col text-dark-gray text-[16px] capitalize"
                                >
                                  {day.label}{" "}
                                  {priceFormatter(dailySubsidy[day.key])}
                                </small>
                              ))}
                          </div>
                        ) : (
                          `Weekly ${priceFormatter(weeklySubsidy)}`
                        )}
                      </div>

                      {subsidyType == "weekly" && (
                        <small className="font-inter-regular text-dark-gray text-[16px] bg-light-gray mt-[8px] px-[8px] rounded-lg">
                          Updating the weekly subsidy will immediately adjust
                          the user's remaining balance for the current week,
                          based on what they’ve already spent. The new subsidy
                          will apply to all upcoming meals in the series after
                          the date selected. If meals have already been placed
                          on that week, only the remaining balance will be
                          updated.
                        </small>
                      )}
                    </span>

                    <strong className="text-dark-gray font-inter-semibold text-[16px] my-[16px]">
                      When should they take effect?
                    </strong>

                    <div className="d-col rounded-lg">
                      <div key="next" className="flex items-center">
                        <input
                          name="date-method"
                          type="radio"
                          value="next"
                          checked={changeEffect == "next"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="photo"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Next available meal in series:{" "}
                          {nextAvailableMeal?.mealDate}
                        </label>
                      </div>

                      <div key="custom" className="flex items-center mt-[16px]">
                        <input
                          name="date-method"
                          type="radio"
                          value={"custom"}
                          checked={changeEffect == "custom"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="signature"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Pick a custom date
                        </label>
                      </div>
                    </div>

                    {changeEffect == "custom" && (
                      <div
                        className="flex flex-row justify-center items-center mt-[16px]"
                        ref={calendarRef}
                      >
                        <Calendar
                          highlightToday={true}
                          className="shadow-lg"
                          isMulti={false}
                          onChange={handleDateChange}
                          disableYearPicker={true}
                          monthYearSeparator={" "}
                          mapDays={({ date }) => {
                            const allowedDates = validMeals?.map(
                              (meal) => meal?.meal_date
                            );
                            const formatted = dayjs(date.toDate()).format(
                              "YYYY-MM-DD"
                            );
                            const isEnabled = allowedDates.includes(formatted);

                            return {
                              disabled: !isEnabled,
                              style: isEnabled
                                ? { color: "#000" }
                                : {
                                    color: "#ccc",
                                    textDecoration: "",
                                    pointerEvents: "none",
                                  },
                            };
                          }}
                        />
                      </div>
                    )}

                    <small className="text-secondary text-[16px] font-inter-regular mt-[16px] mb-[32px]">
                      Subsidy changes can only be made for the next available
                      day where no restaurants have been assigned yet. If you
                      need to make this change sooner contact us for support.{" "}
                    </small>
                  </div>

                  <div className="flex justify-center items-center mt-5">
                    <button
                      className="btn-outline btn-style w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className={classNames({
                        "btn-purple btn-style px-6 ml-3 w-full": true,
                        "btn-disabled":
                          changeEffect === "custom" && !selectedCustomDate,
                      })}
                      onClick={onSavePress}
                      disabled={changeEffect == "custom" && !selectedCustomDate}
                    >
                      Request changes
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className="d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all">
                  <div className="d-row justify-between items-center mb-[8px]">
                    <h2 className="text-[32px] text-dark-gray font-inter-bold">
                      {`Custom ${bulkUsers?.length > 1 ? "Users" : "User"}
                      Subsidy`}
                    </h2>

                    <button
                      onClick={() => setModal(false)}
                      className="text-dark-gray hover:text-yellow-600"
                      aria-label="Close"
                    >
                      <span className="text-xl">&times;</span>
                    </button>
                  </div>

                  <div className="bg-[#F3F3F3] d-col py-4 px-2 rounded-lg">
                    <small className="text-heading-gray font-inter-regular text-base">
                      {mealPlan?.attributes?.subsidy_type == "daily"
                        ? "You can add a custom subsidy for users that will override them from using the meal plan’s subsidy. This meal plan’s subsidy is currently set to:"
                        : "You can add a custom subsidy for users that will override them from using the meal plan’s subsidy. This meal plan’s subsidy"}
                    </small>

                    <small className="text-heading-gray font-inter-medium text-[16px] mt-1">
                      {!mealPlan?.attributes?.subsidy_type
                        ? "Default"
                        : mealPlan?.attributes?.subsidy_type == "daily"
                        ? "Subsidy Per Meal:"
                        : "Subsidy Per Week:"}
                    </small>

                    {!mealPlan?.attributes?.subsidy_type ? (
                      <small className="text-heading-gray font-inter-semibold text-base">
                        {priceFormatter(mealPlan?.attributes?.standard_subsidy)}
                      </small>
                    ) : mealPlan?.attributes?.subsidy_type == "daily" ? (
                      <ol>
                        {Object.entries(
                          mealPlan?.attributes?.daily_subsidy
                        ).map(([day, amount]) => {
                          const isEnabled =
                            mealPlan?.attributes?.days?.includes(day);

                          if (!isEnabled) return;

                          return (
                            <li
                              key={day}
                              className="font-inter-medium text-dark-gray capitalize text-xs"
                            >
                              {day?.substring(0, 3)}: {priceFormatter(amount)}
                            </li>
                          );
                        })}
                      </ol>
                    ) : (
                      <small className="text-heading-gray font-inter-semibold text-base">
                        {priceFormatter(mealPlan?.attributes?.subsidy)}
                      </small>
                    )}
                  </div>

                  <div className="my-3">
                    {mealPlan?.attributes?.subsidy_type == "daily" && (
                      <div className="d-row bg-background rounded-lg items-center mb-3">
                        <button
                          className={classNames({
                            "rounded-xl py-3 text-center font-inter-medium w-full text-xs mr-2": true,
                            "btn-purple": subsidyType == "daily",
                            "text-heading-gray": subsidyType != "daily",
                          })}
                          onClick={() => {
                            setSubsidyType("daily");
                          }}
                        >
                          Custom Subsidy Per Meal Day
                        </button>

                        <button
                          className={classNames({
                            "items-center py-3 font-inter-medium text-center w-full rounded-xl text-xs": true,
                            "btn-purple": subsidyType == "weekly",
                            "text-heading-gray": subsidyType != "weekly",
                          })}
                          onClick={() => {
                            setSubsidyType("weekly");
                          }}
                        >
                          Custom Subsidy Per Week
                        </button>
                      </div>
                    )}

                    {subsidyType == "daily" ? (
                      <small className="font-inter-regular text-base text-heading-gray">
                        Choose the days you’d like to set a custom subsidy for.
                        Days left unchecked will use the default meal plan
                        subsidy. To exclude a subsidy on a specific day, enter
                        $0.
                      </small>
                    ) : (
                      <small className="font-inter-regular text-base text-heading-gray">
                        If you set a custom weekly subsidy, it will replace the
                        default meal plan subsidy for the entire week. Users can
                        spend their weekly subsidy across multiple days, and it
                        may run out before the week ends. Weekly subsidies reset
                        every Monday at 12 AM.
                      </small>
                    )}

                    {subsidyType === "daily" ? (
                      <div className="d-col p-4 rounded-lg">
                        <div className="d-row">
                          <div className="w-1/3 d-row items-start">
                            <span className="text-base font-inter-regular text-heading-gray capitalize">
                              Select day
                            </span>
                          </div>

                          <div className="d-row items-start w-3/4">
                            <span className="text-base font-inter-regular text-heading-gray capitalize">
                              Enter $ Custom Subsidy Amount for User(s)
                            </span>
                          </div>
                        </div>

                        <div className="bg-accent-gray h-0.5 my-4" />

                        <div className="pb-4 px-3">
                          {Object.keys(dailySubsidy)?.map((day) => {
                            const isEnabled =
                              mealPlan?.attributes?.days?.includes(day);
                            const hasError =
                              toggleStates[day] &&
                              (dailySubsidy[day] === "" ||
                                dailySubsidy[day] == null);

                            return (
                              <div
                                key={day}
                                className="text-center d-row items-center rounded mb-4"
                              >
                                <div className="w-1/3 d-row items-start">
                                  <ToggleButton
                                    toggleState={toggleStates[day]}
                                    setToggleState={(value) =>
                                      onToggleChange(day, value)
                                    }
                                    disabled={!isEnabled}
                                  />
                                  <span
                                    className={classNames(
                                      "text-sm ml-2 font-inter-regular capitalize",
                                      !isEnabled
                                        ? "text-gray-400"
                                        : "text-heading-gray"
                                    )}
                                  >
                                    {day}
                                  </span>
                                </div>

                                <div className="text-center rounded w-3/4">
                                  <div
                                    className={classNames(
                                      "border d-row px-2 py-2 mt-1 rounded-lg items-center",
                                      {
                                        "bg-light-gray": !isEnabled,
                                        "bg-white": isEnabled,
                                        "border-red-500":
                                          toggleStates[day] &&
                                          isEnabled &&
                                          (dailySubsidy[day] === "" ||
                                            dailySubsidy[day] == null),
                                        "border-gray-300": !(
                                          toggleStates[day] &&
                                          isEnabled &&
                                          (dailySubsidy[day] === "" ||
                                            dailySubsidy[day] == null)
                                        ),
                                        "opacity-50":
                                          !toggleStates[day] && isEnabled,
                                      }
                                    )}
                                  >
                                    {isEnabled && toggleStates[day] && (
                                      <small className="text-base text-heading-gray mr-1 font-inter-regular">
                                        $
                                      </small>
                                    )}

                                    <input
                                      className="w-full text-base bg-transparent disabled:bg-transparent font-inter-regular focus:outline-none h-[40px] text-heading-gray"
                                      type="number"
                                      disabled={
                                        !isEnabled || !toggleStates[day]
                                      }
                                      value={
                                        isEnabled && toggleStates[day]
                                          ? dailySubsidy[day]
                                          : ""
                                      }
                                      onChange={(e) => {
                                        if (e?.target?.value > -1)
                                          onChangeDaily(e?.target?.value, day);
                                      }}
                                      onWheelCapture={(e) => {
                                        e.target.blur();
                                      }}
                                      onKeyPress={preventMinus}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="d-col py-4 rounded-lg">
                        {bulkUserUpdate && (
                          <small className="font-inter-normal text-dark-gray text-sm mb-2">
                            Enter $ Custom Subsidy Amount for User(s)
                          </small>
                        )}
                        <div
                          className={classNames(
                            "focus:outline-none border bg-white w-full py-3 px-2 rounded-md",
                            {
                              "border-red-500": weeklySubsidyError,
                            }
                          )}
                        >
                          <small className="text-base mr-1 font-inter-medium">
                            $
                          </small>

                          <input
                            autoFocus
                            className="text-base font-inter-medium bg-transparent h-full focus:outline-none text-black"
                            type="number"
                            value={weeklySubsidy}
                            onChange={(e) => {
                              setWeeklySubsidyError(false);
                              if (e?.target?.value > -1)
                                setWeeklySubsidy(e.target?.value);
                            }}
                            onKeyPress={preventMinus}
                            onWheelCapture={(e) => {
                              e.target.blur();
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-col justify-start items-start gap-[7px] mt-[24px] flex">
                    <label className="relative inline-flex items-center cursor-pointer gap-2.5">
                      <input
                        disabled={defaultLocation?.disableCreditCards}
                        type="checkbox"
                        checked={
                          defaultLocation?.disableCreditCards
                            ? false
                            : overPayment
                        }
                        className="sr-only peer"
                        onChange={(e) => setEnableOverpayment(e.target.checked)}
                      />

                      <div className=" w-9 h-5 p-0.5 bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:start-[2px] after:bg-white after:w-4 after:h-4 after:shadow after:border after:rounded-full after:transition-all  peer-checked:bg-indigo-500"></div>

                      <span className="text-slate-700 text-sm font-inter-medium leading-none">
                        Overpayment
                      </span>
                    </label>

                    <div className="justify-start items-start gap-1 mt-[14px] inline-flex">
                      <img
                        src={InfoButtonGray}
                        className="w-3 h-3"
                        alt="DangerIcon"
                      />

                      <div className="w-full text-slate-500 text-xs font-inter-normal leading-none">
                        Allow users to use their personal credit card when going
                        over set allowance to change your settings, please
                        contact your hungerhub account admin.
                      </div>
                    </div>
                  </div>

                  <div className="d-row w-full items-center mt-[24px] justify-center">
                    <button
                      className="btn-light w-full btn-style py-3 px-5"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-purple w-full btn-style py-3 px-6 ml-3"
                      onClick={onSavePress}
                    >
                      Continue
                    </button>
                  </div>
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default observer(ChangeSubsidyMealPlanUsers);
